import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import isNil from 'lodash/isNil';
import queryString from 'query-string';
import { changeLoanFormat } from '../../store/loanFormat/actions';
import AgeCategorySelector from '../../shared/common/AgeCategorySelector';
import LoanFormatDropDown from '../../shared/navigation/LoanFormatDropDown';
import { getLoanFormatFromCollectionUrl } from '../../shared/utils/utils-url-to-loanformat';
import SubNavigationLayout from '../../shared/navigation/SubNavigationLayout';
import { LinkContainer } from '../../shared/navigation/NavigationLinksContainer';
import { AppDispatch } from '../../store';
import { AppState } from '../../store/appState';
import { NavigationLink } from '../../shared/navigation/NavigationLink';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { MATERIAL_ICON_ARROW_FORWARD } from '../../shared/common/Button/Icon';
import { useTranslation } from 'react-i18next';

type Props = {
    readonly isSticky?: boolean;
    readonly showAgeSelector?: boolean;
};

const CollectionNavigation: FC<Props> = ({ isSticky, showAgeSelector = true }) => {
    const { pathname } = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const loanFormat = useSelector((state: AppState) => state.loanFormat);

    useEffect(() => {
        const loanFormatUrl = getLoanFormatFromCollectionUrl(pathname);
        if (!isNil(loanFormatUrl) && loanFormat.key !== loanFormatUrl.key) {
            dispatch(changeLoanFormat(loanFormatUrl));
        }
    }, [dispatch, loanFormat.key, pathname]);

    const loanFormatUrlKey = loanFormat.urlKey;

    const {
        ageCategory: { value: ageCategoryQueryParam },
        display: { value: displayQueryParam }
    } = useQueryParams({
        ageCategory: 'string',
        display: 'string'
    });

    const urlQueryParams = queryString.stringify({ ageCategory: ageCategoryQueryParam, display: displayQueryParam });
    const { t } = useTranslation();
    return (
        <SubNavigationLayout
            left={<LoanFormatDropDown showIcons={true} />}
            center={
                <LinkContainer>
                    <li>
                        <NavigationLink to={`/collection/complete/${loanFormatUrlKey}?${urlQueryParams}`}>
                            {t('menu.yourBorrowbox.yourCollection.completeCollection')}
                        </NavigationLink>
                    </li>
                    <li>
                        <NavigationLink to={`/collection/newly-added/${loanFormatUrlKey}?${urlQueryParams}`}>
                            {t('menu.yourBorrowbox.yourCollection.newlyAdded')}
                        </NavigationLink>
                    </li>
                    <li>
                        <NavigationLink to={`/collection/by-genre/${loanFormatUrlKey}?${urlQueryParams}`}>
                            {t('menu.yourBorrowbox.yourCollection.byGenre')}
                        </NavigationLink>
                    </li>
                    <li style={{ position: 'relative' }}>
                        <NavigationLink to={'/assistant/reserves'}>
                            {t('menu.yourBorrowbox.yourCollection.orderingAssistant')}{' '}
                            <span className="material-icons">{MATERIAL_ICON_ARROW_FORWARD}</span>
                        </NavigationLink>
                    </li>
                </LinkContainer>
            }
            right={showAgeSelector && !isSticky && <AgeCategorySelector />}
            isSticky={isSticky}
            width="20"
        />
    );
};

export default CollectionNavigation;
